import type { AttentionPoint } from "./intelligence/executive";
import { explainError } from "./sync";

/**
 * Leituras da tela Configurações: se o BI está ligado ao ERP e ao WhatsApp, e o
 * que fazer quando não está. Os dados vêm das mesmas consultas das abas
 * (/data-sources, /whatsapp/instances, /users), com as mesmas queryKeys.
 */
export type SettingsDataSource = {
  id: string;
  name: string;
  connectionStatus: string;
  lastProbeAt: string | null;
  lastProbeError: string | null;
  externalCompanyCodes: number[];
};

export type SettingsInstance = { id: string; instanceName: string; status: "connected" | "disconnected" | "pending" | "error" };

export type SettingsSummary = {
  sources: number;
  sourcesOk: number;
  sourcesError: SettingsDataSource[];
  sourcesUntested: SettingsDataSource[];
  companyCodes: number[];
  instances: number;
  instancesOk: number;
};

export function summarizeSettings(sources: SettingsDataSource[], instances: SettingsInstance[]): SettingsSummary {
  return {
    sources: sources.length,
    sourcesOk: sources.filter((d) => d.connectionStatus === "ok").length,
    sourcesError: sources.filter((d) => d.connectionStatus === "error"),
    sourcesUntested: sources.filter((d) => d.connectionStatus !== "ok" && d.connectionStatus !== "error"),
    companyCodes: [...new Set(sources.flatMap((d) => d.externalCompanyCodes ?? []))].sort((a, b) => a - b),
    instances: instances.length,
    instancesOk: instances.filter((i) => i.status === "connected").length,
  };
}

/**
 * Fila "O que pede ação", no formato da Visão Executiva. `instances` nulo = a
 * consulta do WhatsApp não respondeu (sem acesso): não acusa desconexão.
 */
export function settingsAttention(sum: SettingsSummary, instances: SettingsInstance[] | null): AttentionPoint[] {
  const pts: Omit<AttentionPoint, "priority">[] = [];
  if (!sum.sources) pts.push({
    id: "sem-erp", severity: "critical", memory: null,
    title: "Nenhuma conexão com o ERP",
    fact: "O BI não tem banco do ERP cadastrado, então nenhum painel recebe dado novo.",
    impact: "Painéis sem atualização",
    action: "Cadastre a conexão na aba Bancos de Dados e use Testar conexão.",
    evidence: [],
  });
  for (const d of sum.sourcesError) pts.push({
    id: `erp-erro-${d.id}`, severity: "critical", memory: null,
    title: "Conexão com o ERP falhou no último teste",
    fact: `${d.name}: ${explainError(d.lastProbeError) ?? "erro ao conectar"}.`,
    impact: "A sincronização não alcança o ERP",
    action: "Confira o servidor e a senha na aba Bancos de Dados e teste de novo.",
    evidence: [{ label: "Último teste", value: d.lastProbeAt ? new Date(d.lastProbeAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—" }],
  });
  if (sum.sourcesUntested.length) pts.push({
    id: "erp-nao-testado", severity: "info", memory: null,
    title: sum.sourcesUntested.length > 1 ? "Conexões ainda não testadas" : "Conexão ainda não testada",
    fact: `${sum.sourcesUntested.map((d) => d.name).join(", ")} ${sum.sourcesUntested.length > 1 ? "foram salvas" : "foi salva"} sem teste depois da última alteração.`,
    impact: "Situação desconhecida",
    action: "Use Testar conexão na aba Bancos de Dados.",
    evidence: sum.sourcesUntested.map((d) => ({ label: d.name, value: "não testado" })),
  });
  if (instances && sum.instances && !sum.instancesOk) pts.push({
    id: "whatsapp-desconectado", severity: "warning", memory: null,
    title: "WhatsApp desconectado",
    fact: `${sum.instances > 1 ? `Nenhuma das ${sum.instances} instâncias está conectada` : "A instância do WhatsApp não está conectada"}.`,
    impact: "Alertas e perguntas pelo WhatsApp param",
    action: "Reconecte na aba WhatsApp (ler o QR Code com o celular da empresa).",
    evidence: instances.map((i) => ({ label: i.instanceName, value: i.status === "pending" ? "aguardando QR Code" : i.status === "error" ? "com erro" : "desconectada" })),
  });
  if (instances && !sum.instances) pts.push({
    id: "whatsapp-ausente", severity: "info", memory: null,
    title: "WhatsApp não configurado",
    fact: "Não há instância do WhatsApp cadastrada.",
    impact: "Sem alertas e perguntas pelo WhatsApp",
    action: "Cadastre a instância na aba WhatsApp, se o canal for usado.",
    evidence: [],
  });
  return pts.map((p, i) => ({ ...p, priority: i + 1 }));
}
